/**
 * BrandDetailPresenter
 * --
 */
import React, { useState, useMemo } from 'react';
import { Content, Row, Col, Title, ModalLayout } from 'components';
import {
  Select,
  Table,
  Input,
  Button,
  Tag,
  Spin,
  Divider,
  Space,
  Flex,
  Descriptions,
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { stringToMoneyFormat } from 'utils';
import moment from 'moment';
import {
  PRODUCT_TYPE,
  PRODUCT_STATUS,
  APPLICATION_STATUS,
} from 'constants/ProductConstants';
import { APPLICATION_STATUS as SHOP_APPLICATION_STATUS } from 'constants/ShopInfomationConstants';
import { EditOutlined, DownOutlined } from '@ant-design/icons';
import ProductDetail from 'routes/admin.routes/admin.products/ProductDetail';
import { useUserStack } from 'store';
import { downloadXlsx } from 'utils/XlsxDownloader';
import ShopUploadFile from './components/ShopUploadFile';
import dayjs from 'dayjs';
import InformationContainer from 'routes/shop.routes/shop.information/InformationContainer';

const BRAND_STATUS = [
  {
    label: '브랜드',
    value: 'Y',
  },
  {
    label: '입점업체',
    value: 'N',
  },
];

/**----------------------------------------
 * [Component] 입점업체 상세: 프레젠터
 * ----------------------------------------
 */
const BrandDetailPresenter = ({
  loading,
  shopId,
  shopInfomation,
  productList,
  onChangeApplicationStatus,
  onChangeProduct,
  shopInformationFiles,
  onFileDownload,
  onRefetchData,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const userInfo = useUserStack();

  /* ===== STATE ===== */
  const [feedbackText, setFeedbackText] = useState('');
  const [productFeedback, setProductFeedback] = useState('');
  const [productFeedbackModal, setProductFeedbackModal] = useState(null);
  const [productModal, setProductModal] = useState(null);
  const [searchText, setSearchText] = useState(''); // 검색 텍스트 상태

  const [editModal, setEditModal] = useState(null);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  // console.log('shopInfomation: ', shopInfomation);
  // console.log('productList: ', productList);

  const filteredProducts = useMemo(() => {
    return productList?.length > 0
      ? productList?.filter(
          (product) =>
            product?.product_code
              .toLowerCase()
              ?.includes(searchText.toLowerCase()) ||
            product?.product_name
              .toLowerCase()
              ?.includes(searchText.toLowerCase())
        )
      : [];
  }, [productList, searchText]);

  const col = [
    {
      title: '상품코드',
      dataIndex: 'product_code',
      width: 150,
      align: 'center',
    },
    {
      title: '상품명',
      dataIndex: 'product_name',
      // width: 200,
      align: 'center',
      render: (v, record) => (
        <a
          href={`/#/products/${record.product_code}`}
          style={{ textDecoration: 'underline' }}
          onClick={(e) => {
            e.preventDefault();
            setProductModal(record);
            // window.open(
            //   e.currentTarget.href,
            //   '_blank',
            //   'width=860, height=620'
            // );
          }}
        >
          {v}
        </a>
      ),
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => confirm()}
            placeholder={`상품명 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setSearchText(''); // 초기화 시 검색 텍스트 비우기
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },

    {
      title: '상품유형',
      dataIndex: 'type',
      width: 120,
      align: 'center',
      render: (v) => PRODUCT_TYPE[v].title,
    },
    {
      title: '금액',
      dataIndex: 'price',
      width: 110,
      align: 'center',
      render: (v) => (
        <p style={{ textAlign: 'right' }}>{stringToMoneyFormat(v)}</p>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '옵션',
      dataIndex: 'product_option',
      width: 100,
      align: 'center',
      render: (v) => `${v?.length ?? 0}개`,
    },

    {
      title: '노출여부',
      dataIndex: 'is_show',
      width: 100,
      align: 'center',
      render: (v) => (v === 'Y' ? '노출' : '미노출'),
    },
    {
      title: '상태',
      dataIndex: 'product_status',
      width: 100,
      align: 'center',
      render: (v) => {
        const { color, title } = PRODUCT_STATUS[v];
        return <Tag color={color}>{title}</Tag>;
      },
    },
    {
      title: '판매상태',
      width: 130,
      dataIndex: 'product_status',
      align: 'center',
      render: (v, record) => {
        return (
          <>
            <Select
              value={v}
              style={{
                width: '100%',
              }}
              options={Object.keys(PRODUCT_STATUS).map((value) => ({
                label: PRODUCT_STATUS[value]?.title,
                value,
              }))}
              onChange={(value) =>
                onChangeProduct(record.product_id, 'product_status', value)
              }
            />
          </>
        );
      },
    },
    {
      title: '등록신청',
      dataIndex: 'application_status',
      width: 100,
      align: 'center',
      render: (v) => {
        const { color, title } = APPLICATION_STATUS[v];
        return <Tag color={color}>{title}</Tag>;
      },
    },
    {
      title: '등록신청 심사',
      dataIndex: 'application_status',
      width: 130,
      align: 'center',
      render: (v, record) => {
        return (
          <>
            <Select
              value={v}
              style={{
                width: '100%',
              }}
              options={Object.keys(APPLICATION_STATUS).map((value) => ({
                label: APPLICATION_STATUS[value].title,
                value,
              }))}
              onChange={(value) =>
                onChangeProduct(record.product_id, 'application_status', value)
              }
            />
          </>
        );
      },
    },
    {
      title: '피드백',
      dataIndex: 'apply_feedback',
      width: 140,
      align: 'center',
      render: (v, record) => (
        <>
          <Button
            color="primary"
            variant="solid"
            onClick={() =>
              setProductFeedbackModal({
                product_id: record.product_id,
                apply_feedback: v,
              })
            }
          >
            <EditOutlined /> 등록
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Spin spinning={loading}>
        <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
          <Flex
            justify="space-between"
            align="center"
            style={{ marginBottom: 10 }}
          >
            <Title size={5}>업체정보</Title>
            <Button
              type="primary"
              onClick={() => setEditModal(shopInfomation?.shop_information_id)}
            >
              수정
            </Button>
          </Flex>

          {shopInfomation && (
            <Descriptions
              className="boxShadow"
              size="small"
              column={3}
              bordered
              labelStyle={{ width: '12%', height: '58px' }}
              contentStyle={{ width: '18%', textAlign: 'center' }}
              items={[
                {
                  key: 'shop_information_name',
                  label: '업체명',
                  children: shopInfomation.shop_information_name,
                },
                {
                  key: 'shop_information_postcode',
                  label: '업체코드',
                  children: shopInfomation.shop_information_postcode,
                },
                {
                  key: 'updated_at',
                  label: '등록일',
                  children: moment(shopInfomation.updated_at * 1000).format(
                    'YYYY-MM-DD(HH:mm:ss)'
                  ),
                },
                //
                {
                  key: 'business_hour',
                  label: '운영시간',
                  children: shopInfomation?.mall_information?.business_hour,
                },
                {
                  key: 'store_owner',
                  label: '대표자명',
                  children: shopInfomation?.mall_information?.store_owner,
                },
                {
                  key: 'store_num',
                  label: '연락처',
                  children: shopInfomation?.mall_information?.store_num,
                },
                //
                {
                  key: 'store_status',
                  label: '영업',
                  children: shopInfomation?.store_status,
                },
                {
                  key: 'is_brand',
                  label: '브랜드여부',
                  children: (
                    <Select
                      defaultValue={shopInfomation?.is_brand}
                      style={{ width: 145 }}
                      onChange={(v) => onChangeApplicationStatus('is_brand', v)}
                      options={BRAND_STATUS}
                    />
                  ),
                },
                {
                  key: 'business_num_value',
                  label: '사업자번호',
                  children: shopInfomation?.mall_information?.business_num,
                },
                //
                {
                  key: 'thumbnail',
                  label: '브랜드관 썸네일',
                  children: (
                    <ShopUploadFile
                      file={shopInformationFiles?.thumbnail}
                      type="THUMBNAIL"
                      shopId={shopId}
                    />
                  ),
                },
                {
                  key: 'detail_image',
                  label: '브랜드관 상세이미지',
                  span: 2,
                  children: (
                    <ShopUploadFile
                      file={shopInformationFiles?.detail}
                      type="STORE"
                      shopId={shopId}
                    />
                  ),
                },
                //
                {
                  key: 'business_license',
                  label: '사업자등록증',
                  span: 1.5,
                  children: (
                    <Flex justify="center" align="center" gap={24}>
                      <div style={{ maxHeight: 150 }}>
                        <ShopUploadFile
                          file={shopInformationFiles?.license}
                          type="LICENSE"
                          shopId={shopId}
                        />
                      </div>
                      {shopInformationFiles?.license && (
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() =>
                            onFileDownload(
                              shopInformationFiles.license.file_url,
                              `${
                                shopInfomation?.shop_information_name
                              }-${dayjs().unix()}`
                            )
                          }
                        >
                          파일 다운로드
                        </Button>
                      )}
                    </Flex>
                  ),
                },
                {
                  key: 'shop_information_address',
                  label: '주소',
                  span: 1.5,
                  children: `[${shopInfomation?.shop_information_postcode}] ${shopInfomation?.shop_information_address} (${shopInfomation?.shop_information_address_detail})`,
                },
                //
                {
                  key: 'bank_name',
                  label: '입금은행',
                  children: shopInfomation?.mall_information?.bank_name ?? '-',
                },
                {
                  key: 'account_number',
                  label: '입금계좌',
                  children:
                    shopInfomation?.mall_information?.account_number ?? '-',
                },
                {
                  key: 'application_status',
                  label: '심사여부',
                  children:
                    shopId === 1 ? (
                      SHOP_APPLICATION_STATUS[
                        shopInfomation?.application_status
                      ]?.title
                    ) : (
                      <Select
                        defaultValue={shopInfomation?.application_status}
                        style={{ width: 145 }}
                        onChange={(v) =>
                          onChangeApplicationStatus('application_status', v)
                        }
                        options={Object.keys(SHOP_APPLICATION_STATUS).map(
                          (value) => ({
                            label: SHOP_APPLICATION_STATUS[value].title,
                            value,
                          })
                        )}
                      />
                    ),
                },
                //
                // {
                //   key: 'feedback',
                //   label: '피드백',
                //   span: 3,
                //   children: (
                //     <div
                //       style={{
                //         display: 'flex',
                //         justifyContent: 'space-between',
                //       }}
                //     >
                //       <div style={{ flex: 5 }}>
                //         <Input.TextArea
                //           defaultValue={
                //             shopInfomation?.edit_information?.feedbackText?.[0]
                //           }
                //           placeholder="내용을 입력해주세요"
                //           autoSize={{ minRows: 3, maxRows: 3 }}
                //           onChange={(e) => setFeedbackText(e.target.value)}
                //         />
                //       </div>
                //       <div style={{ flex: 1 }}>
                //         <Button
                //           style={{ width: 80, height: '100%' }}
                //           onClick={() => {
                //             onChangeApplicationStatus('edit_information', {
                //               ...shopInfomation?.edit_information,
                //               feedbackText: [
                //                 feedbackText,
                //                 ...(shopInfomation?.edit_information
                //                   ?.feedbackText ?? []),
                //               ],
                //             });
                //           }}
                //         >
                //           저장
                //         </Button>
                //       </div>
                //     </div>
                //   ),
                // },
              ]}
            />
          )}

          <br />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Title size={5}>등록상품 ({filteredProducts.length})</Title>
            <Button
              onClick={() =>
                downloadXlsx({
                  columns: [
                    ...col
                      ?.map((_item) => {
                        if (!_item?.dataIndex && !_item?.key) return null;
                        return _item;
                      })
                      ?.filter((_item) => _item !== null),
                    {
                      title: '피드백',
                      key: 'apply_feedback',
                    },
                  ],

                  rows: filteredProducts?.map((_item) => {
                    return {
                      ..._item,
                      product_option: _item?.product_option?.length ?? 0,
                    };
                  }),
                  fileName: `입점업체_상품목록`,
                })
              }
            >
              엑셀로 다운로드
            </Button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table
              bordered
              size="small"
              style={{ minWidth: 1500, width: '100%' }}
              dataSource={filteredProducts ?? []}
              columns={col}
            />
          </div>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                {/* <Empty
              style={{
                height: 500,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
              description={
                <>
                  <Title align={'center'}>개발중입니다.</Title>
                </>
              }
            ></Empty> */}
                {/* {JSON.stringify(productList)} */}
              </Content>
            </Col>
          </Row>
        </Content>

        {/* ===== 피드백 등록 모달 ===== */}
        <ModalLayout
          width={400}
          maxWidth={'calc(100% - 250px)'}
          isOpen={productFeedbackModal !== null}
          open={productFeedbackModal !== null}
          title="상품 피드백 등록"
          closable={true}
          onCancel={() => setProductFeedbackModal(null)}
          footer={
            <>
              <Button
                type="primary"
                onClick={() => {
                  onChangeProduct(
                    productFeedbackModal.product_id,
                    'apply_feedback',
                    productFeedback
                  );
                  setProductFeedbackModal(null);
                  setProductFeedback('');
                }}
              >
                등록완료
              </Button>
            </>
          }
        >
          <Divider style={{ margin: '14px 0' }} />
          {productFeedbackModal && (
            <div>
              <p>이전 피드백: {productFeedbackModal.apply_feedback}</p>
              <Input
                placeholder="피드백을 입력해주세요"
                value={productFeedback}
                onChange={(e) => setProductFeedback(e.target.value)}
              />
            </div>
          )}
        </ModalLayout>
      </Spin>

      {/* 상품상세보기 */}
      <ModalLayout
        // type="drawer"
        centered
        // placement="bottom"
        title={'상품상세보기'}
        open={productModal !== null}
        onCancel={() => setProductModal(null)}
        closable
        width={'95%'}
        height={'95%'}
      >
        {productModal && (
          <ProductDetail
            permission={userInfo.type}
            defaultShopId={userInfo.shop_information_id}
            defaultProductId={productModal.product_id}
          />
        )}
      </ModalLayout>

      {/* 업체 수정모달 */}
      <ModalLayout
        centered
        // placement="bottom"
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        closable
        width={'50%'}
      >
        <InformationContainer
          shopId={editModal}
          direction={'vertical'}
          refetch={onRefetchData}
        />
      </ModalLayout>
    </>
  );
};

export default BrandDetailPresenter;
